import React, { useState, useCallback, useMemo, useEffect, useContext, useRef } from "react";
import {
  Box,
  Tabs,
  Tab,
  Button,
  TextField,
  InputAdornment,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import IconButton from "@mui/material/IconButton";
import SearchIcon from "@mui/icons-material/Search";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import { getPayoutColumns, getMappingColumns } from "../../../Data/Configuration/Payout";
import PayoutForm from "../Forms/PayoutForm";
import CreateEditPayoutForm from "../Forms/CreateEditPayoutForm";
import CreateEditPayoutMappingForm from "../Forms/CreateEditPayoutMappingForm";
import { getAllDriverPayouts, getPackagePayoutMappings, deletePackagePayoutMapping } from "../../../Services/Payout.service.jsx";
import {
  GREEN_BUTTON_STYLE,
  TABS_STYLE,
  TAB_ITEM_STYLE,
  SEARCH_FIELD_STYLE,
} from "../../../styles/CommonStyles";
import { PAYOUT_DETAIL_FIELDS } from "../../../Data/Configuration/Payout";
import SharedDataContext from "../../../_helper/SharedData";
import FilterTable from "../../Common/Component/FilterTable";
import SelectedFilter from "../../Driver/DriverListing/SelectedFilter";
import { Row } from "reactstrap";
import DeleteConfirmDialog from "../../Common/Component/DeleteConfirmDialog";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Paper from "@mui/material/Paper";
import Skeleton from "@mui/material/Skeleton";
import Collapse from "@mui/material/Collapse";


/**
 * Expandable row for the Tariff & Payout Settings tab.
 * Shows Initial Distance, Initial Distance Charge, Per Km Charge in a collapsible section.
 */
const MappingExpandableRow = ({ row, columns }) => {
  const [open, setOpen] = React.useState(false);
  const payout = row.driverPayout || {};
  const totalCols = columns.length;

  return (
    <>
      <TableRow
        sx={{
          "&:hover": { backgroundColor: "#EBEBEB" },
          "& td": { borderBottom: "none" },
        }}
      >
        {/* First cell: expand arrow + partner name */}
        <TableCell
          sx={{
            color: columns[0].fontColor || '#313131',
            fontWeight: columns[0].fontWeight || '400',
            fontSize: "0.95vw",
            padding: columns[0].padding || "16px",
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton size="small" onClick={() => setOpen(!open)} sx={{ p: '2px' }}>
              {open ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
            </IconButton>
            {columns[0].renderCell
              ? columns[0].renderCell({ row, value: row[columns[0].field], field: columns[0].field })
              : (row[columns[0].field] || '-')}
          </Box>
        </TableCell>

        {/* Remaining cells */}
        {columns.slice(1).map((col) => {
          const cellValue = row[col.field];
          const renderedValue = col.renderCell ? col.renderCell({ row, value: cellValue, field: col.field }) : cellValue;
          return (
            <TableCell
              key={col.field}
              align={col.align || 'left'}
              sx={{
                color: col.fontColor || '#313131',
                fontWeight: col.fontWeight || '400',
                fontSize: "0.95vw",
                whiteSpace: col.noWrap ? "nowrap" : "normal",
                padding: col.padding || "16px",
              }}
            >
              {renderedValue}
            </TableCell>
          );
        })}
      </TableRow>

      {/* Expandable detail row */}
      <TableRow>
        <TableCell colSpan={totalCols} sx={{ paddingBottom: 0, paddingTop: 0, borderBottom: 'none' }}>
          <Collapse in={open} unmountOnExit>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-evenly',
                paddingX: '3vw',
                paddingY: '0.8vw',
                backgroundColor: '#e7e7e7ff',
                borderRadius: '4px',
                marginBottom: '4px',
              }}
            >
              {PAYOUT_DETAIL_FIELDS.map((field) => (
                <Box key={field.label} sx={{ textAlign: 'center' }}>
                  <Typography sx={{ fontSize: '0.82vw', color: '#626262' }}>{field.label}</Typography>
                  <Typography sx={{ fontWeight: 600, fontSize: '0.92vw' }}>{field.accessor(payout)}</Typography>
                </Box>
              ))}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};


const PayoutFlow = () => {
  const { globalCityField } = useContext(SharedDataContext);
  const [activeTab, setActiveTab] = useState(0);

  // Data states
  const [payoutData, setPayoutData] = useState([]);
  const [mappingData, setMappingData] = useState([]);
  const [loading, setLoading] = useState(false);

  // Pagination states
  const [pagination, setPagination] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [paginationTotalRows, setPaginationTotalRows] = useState(0);

  // Search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInputValue, setSearchInputValue] = useState("");
  const searchDebounceRef = useRef(null);
  const [filterData, setFilterData] = useState([]);
  const [filterType, setFilterType] = useState("");
  const fetchInFlightRef = useRef(false);

  // Delete confirmation state
  const [deleteConfirmState, setDeleteConfirmState] = useState({ open: false, row: null, loading: false });

  // Modal State: { type: 'payout' | 'mapping' | null, data: object | null, open: boolean }
  const [modalState, setModalState] = useState({
    type: null,
    data: null,
    open: false,
  });

  const fetchPayouts = useCallback(async () => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setLoading(true);
    try {
      const serviceCityId = globalCityField.value?.id || null;
      const requestBody = {
        isPagination: true,
        search: searchQuery || "",
        ...(serviceCityId && { serviceCityId }),
      };

      if (filterType === "filter" && filterData.length > 0) {
        filterData.forEach((f) => {
          requestBody[f.column_name] = f.filterValues ? f.filterValues[0] : f.values[0];
        });
      }

      const response = await getAllDriverPayouts(requestBody, pagination, rowsPerPage, 'id,desc');
      if (response && response.data) {
        setPayoutData(response.data.list || []);
        setPaginationTotalRows(response.data.count || 0);
      } else {
        setPayoutData([]);
        setPaginationTotalRows(0);
      }
    } catch (error) {
      console.error("Failed to fetch payouts", error);
    } finally {
      setLoading(false);
      fetchInFlightRef.current = false;
    }
  }, [pagination, rowsPerPage, searchQuery, filterData, filterType, globalCityField.value]);

  const fetchMappings = useCallback(async () => {
    if (fetchInFlightRef.current) return;
    fetchInFlightRef.current = true;
    setLoading(true);
    try {
      const serviceCityId = globalCityField.value?.id || null;
      const requestBody = {
        packagesId: null,
        corporateClientId: null,
        driverPayoutId: null,
        isPagination: true,
        search: searchQuery || "",
        ...(serviceCityId && { serviceCityId }),
      };

      if (filterType === "filter" && filterData.length > 0) {
        filterData.forEach((f) => {
          requestBody[f.column_name] = f.filterValues ? f.filterValues[0] : f.values[0];
        });
      }

      const response = await getPackagePayoutMappings(requestBody, pagination, rowsPerPage, 'id,desc');
      if (response && response.data) {
        setMappingData(response.data.list || []);
        setPaginationTotalRows(response.data.count || 0);
      } else {
        setMappingData([]);
        setPaginationTotalRows(0);
      }
    } catch (error) {
      console.error("Failed to fetch mappings", error);
    } finally {
      setLoading(false);
      fetchInFlightRef.current = false;
    }
  }, [pagination, rowsPerPage, searchQuery, filterData, filterType, globalCityField.value]);

  useEffect(() => {
    if (activeTab === 0) {
      fetchPayouts();
    }
  }, [activeTab, pagination, rowsPerPage, fetchPayouts, globalCityField.value]);

  useEffect(() => {
    if (activeTab === 1) {
      fetchMappings();
    }
  }, [activeTab, pagination, rowsPerPage, fetchMappings]);

  // Handlers — prevent double-open by checking if modal is already open
  const handleEdit = useCallback((row, type) => {
    setModalState((previousModalState) => {
      if (previousModalState.open) return previousModalState;
      return { type, data: row, open: true };
    });
  }, []);

  const handleCreate = useCallback((type) => {
    setModalState({ type, data: null, open: true });
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalState({ type: null, data: null, open: false });
  }, []);

  const handleSuccess = useCallback(() => {
    handleCloseModal();
    if (activeTab === 0) {
      fetchPayouts();
    } else {
      fetchMappings();
    }
  }, [activeTab, fetchPayouts, fetchMappings, handleCloseModal]);

  const onChangePage = useCallback((event, newPage) => {
    setPagination(newPage);
  }, []);

  const onChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPagination(0);
  }, []);

  const handleTabChange = useCallback((event, newValue) => {
    setActiveTab(newValue);
    setPagination(0);
    setSearchQuery("");
    setSearchInputValue("");
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setFilterData([]);
    setFilterType("");
  }, []);

  const handleSearchChange = useCallback((event) => {
    const inputValue = event.target.value;
    setSearchInputValue(inputValue);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setSearchQuery(inputValue);
      setFilterType(inputValue ? "search" : "");
      setPagination(0);
    }, 400);
  }, []);

  const removeFilter = useCallback((index) => {
    const newFilterData = [...filterData];
    newFilterData.splice(index, 1);
    setFilterData(newFilterData);
    setPagination(0);
  }, [filterData]);

  const handleDeleteMapping = useCallback((row) => {
    setDeleteConfirmState({ open: true, row, loading: false });
  }, []);

  const handleDeleteConfirm = useCallback(async () => {
    if (deleteConfirmState.loading) return;
    const { row } = deleteConfirmState;
    setDeleteConfirmState((previousState) => ({ ...previousState, loading: true }));
    try {
      await deletePackagePayoutMapping(row.id);
      setDeleteConfirmState({ open: false, row: null, loading: false });
      setMappingData((previousMappings) => previousMappings.filter((mapping) => mapping.id !== row.id));
      setPaginationTotalRows((previousTotal) => Math.max(0, previousTotal - 1));
    } catch (error) {
      console.error("PAYOUT_FLOW_DELETE_MAPPING_FAILED - " + row.id, error);
      setDeleteConfirmState((previousState) => ({ ...previousState, loading: false }));
    }
  }, [deleteConfirmState]);

  const handleDeleteCancel = useCallback(() => {
    setDeleteConfirmState({ open: false, row: null, loading: false });
  }, []);

  const payoutFilterOptions = [
    { value: { name: "Car Type", value: "carTypeId", type: "select" }, label: "Car Type" },
  ];

  // Columns Configuration
  const payoutColumns = useMemo(
    () => getPayoutColumns((row) => handleEdit(row, "payout")),
    [handleEdit]
  );

  const mappingColumns = useMemo(
    () => getMappingColumns((row) => handleEdit(row, "mapping"), handleDeleteMapping),
    [handleEdit, handleDeleteMapping]
  );

  // Active Tab Configuration
  const currentTab = useMemo(() => {
    if (activeTab === 0) {
      return {
        data: payoutData,
        columns: payoutColumns,
        buttonText: "CREATE PAYOUT",
        modalType: "payout",
        emptyMessage: "No payout configurations found",
      };
    }
    return {
      data: mappingData,
      columns: mappingColumns,
      buttonText: "CREATE MAPPING",
      modalType: "mapping",
      emptyMessage: "No partner mappings found",
    };
  }, [
    activeTab,
    payoutData,
    mappingData,
    payoutColumns,
    mappingColumns
  ]);

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2,
        }}
      >
        <Tabs value={activeTab} onChange={handleTabChange} sx={TABS_STYLE}>
          <Tab label="Payouts" sx={TAB_ITEM_STYLE} disableRipple />
          <Tab label="Tariff & Payout Mapping" sx={TAB_ITEM_STYLE} disableRipple />
        </Tabs>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => handleCreate(currentTab.modalType)}
            sx={GREEN_BUTTON_STYLE}
          >
            {currentTab.buttonText}
          </Button>
          <FilterTable
            options={payoutFilterOptions}
            setFilterData={setFilterData}
            filterData={filterData}
            setSearchData={setSearchQuery}
            setFilterType={setFilterType}
            carTypeData={globalCityField.value?.carType?.map(car => ({ value: car.id, label: car.name })) || []}
          />
          <TextField
            size="small"
            placeholder="Search"
            value={searchInputValue}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "#9e9e9e" }} />
                </InputAdornment>
              ),
            }}
            sx={SEARCH_FIELD_STYLE}
          />
        </Box>
      </Box>

      {filterData.length > 0 && (
        <Row className="selected-filter" style={{ marginBottom: "15px" }}>
          {filterData.map((element, i) => (
            <SelectedFilter
              key={i}
              element={element}
              index={i}
              removeFilter={removeFilter}
            />
          ))}
        </Row>
      )}

      {/* Table Component Styled like CorporateClientsList.jsx */}
      <Box
        sx={{
          width: "100%",
          overflowY: "hidden",
          overflowX: "hidden",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        }}
      >
        {loading || currentTab.data.length > 0 ? (
          <>
            <TableContainer
              component={Paper}
              aria-label="sticky table"
              sx={{
                maxHeight: "33vw",
                overflowY: "auto",
                scrollbarWidth: "none",
                overflowX: "hidden",
                "&::-webkit-scrollbar": {
                  display: "none",
                },
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow
                    sx={{
                      "& th": {
                        color: "#626262",
                        fontWeight: "bold",
                        fontSize: "1.0vw",
                        backgroundColor: "#E1F4E4",
                      },
                    }}
                  >
                    {currentTab.columns.map(col => (
                      <TableCell
                        key={col.field}
                        align={col.headerAlign || 'left'}
                        sx={{
                          padding: col.padding || "16px",
                          width: col.width || "auto"
                        }}
                      >
                        {col.headerName}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading
                    ? [...Array(rowsPerPage)].map((_, index) => (
                      <TableRow key={index}>
                        {currentTab.columns.map(col => (
                          <TableCell key={col.field}>
                            <Skeleton animation="wave" width="100%" height={30} />
                          </TableCell>
                        ))}
                      </TableRow>
                    ))
                    : currentTab.data.map((row) =>
                      activeTab === 1 ? (
                        // Mapping tab — expandable accordion row
                        <MappingExpandableRow
                          key={row.id}
                          row={row}
                          columns={currentTab.columns}
                        />
                      ) : (
                        // Payouts tab — flat row
                        <TableRow
                          key={row.id}
                          sx={{
                            "&:hover": { backgroundColor: "#EBEBEB" },
                            "&:last-child td, &:last-child th": { border: 0 },
                            "& td": { borderBottom: "none" },
                          }}
                        >
                          {currentTab.columns.map(col => {
                            let cellValue = row[col.field];
                            let renderedValue = col.renderCell ? col.renderCell({ row, value: cellValue, field: col.field }) : cellValue;
                            return (
                              <TableCell
                                key={col.field}
                                align={col.align || 'left'}
                                sx={{
                                  color: col.fontColor || '#313131',
                                  fontWeight: col.fontWeight || '400',
                                  fontSize: "0.95vw",
                                  whiteSpace: col.noWrap ? "nowrap" : "normal",
                                  padding: col.padding || "16px",
                                }}
                              >
                                {renderedValue}
                              </TableCell>
                            );
                          })}
                        </TableRow>
                      )
                    )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={paginationTotalRows}
              page={pagination}
              onPageChange={onChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={onChangeRowsPerPage}
              rowsPerPageOptions={[7, 10, 25, 50]}
              sx={{
                "& .MuiTablePagination-toolbar": {
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "flex-end",
                  gap: 2,
                  textAlign: "center",
                  overflowX: "hidden",
                },
                "& .MuiTablePagination-select": {
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  borderRadius: "6px",
                  border: "1px solid #D9D9D9",
                },
                "& .MuiTablePagination-actions button": {
                  color: "rgba(112, 112, 112, 1)",
                  transform: "scale(1.3)",
                },
                "& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
                  color: "rgba(98, 98, 98, 1)",
                  fontSize: "0.97vw",
                  textAlign: "center",
                  m: "auto",
                  letterSpacing: "0.02vw",
                },
              }}
            />
          </>
        ) : (
          <Box
            sx={{
              width: "100%",
              backgroundColor: "white",
              minHeight: "200px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              borderRadius: "6px",
            }}
          >
            <Box sx={{ color: "black", fontSize: "1vw" }}>
              {currentTab.emptyMessage}
            </Box>
          </Box>
        )}
      </Box>

      {/* Modals */}
      {/* For Create flows: Show PayoutForm with tabs */}
      {!modalState.data && (
        <PayoutForm
          open={modalState.open}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
          initialTab={activeTab}
        />
      )}

      {/* For Edit Payout: Show CreateEditPayoutForm standalone */}
      {modalState.data && modalState.type === "payout" && (
        <CreateEditPayoutForm
          open={modalState.open}
          onClose={handleCloseModal}
          onSuccess={handleSuccess}
          editData={modalState.data}
          standalone={true}
        />
      )}

      {modalState.data && modalState.type === "mapping" && (
        <Dialog
          open={modalState.open}
          onClose={handleCloseModal}
          maxWidth="md"
          fullWidth
          PaperProps={{ sx: { borderRadius: '12px', boxShadow: '0 8px 24px rgba(0,0,0,0.15)' } }}
        >
          <DialogTitle
            sx={{
              fontWeight: 600,
              fontSize: '1.25rem',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            Edit Mapping
            <IconButton onClick={handleCloseModal} size="small">
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers sx={{ p: 0 }}>
            <CreateEditPayoutMappingForm
              editData={modalState.data}
              onSuccess={handleSuccess}
              onCancel={handleCloseModal}
            />
          </DialogContent>
        </Dialog>
      )}

      {/* Delete Mapping Confirmation */}
      <DeleteConfirmDialog
        open={deleteConfirmState.open}
        title="Remove Payout Mapping"
        message={
          <>
            You are about to permanently remove this payout mapping. This action cannot be undone and will affect driver payout calculations.{" "}
            <strong>Are you sure you want to proceed?</strong>
          </>
        }
        loading={deleteConfirmState.loading}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
      />
    </Box>
  );
};

export default PayoutFlow;
